/**
 * User Timing marks for the render gates a workspace load passes through, so
 * one Mixpanel event can carry the boot broken into stages. Only the first mark
 * of each stage counts: a remount or a refetch is not a boot.
 *
 * Chrome only draws marks in the Performance panel when they carry a
 * `detail.devtools` payload, so each stage is also measured against the one
 * before it into a "Weld boot" track.
 *
 * Reached stages are also tracked in memory, so the once-only guard holds where
 * User Timing is missing and nothing else records the mark.
 */
const REPORTED_STAGES = [
  "render-start",
  "app-mounted",
  "user-ready",
  "flags-ready",
] as const;

// `integrations-ready` closes the last span for DevTools; the event that reads
// these timings already reports that instant as `ms_since_page_load`.
const ORDER = [...REPORTED_STAGES, "integrations-ready"] as const;

export type BootStage = (typeof ORDER)[number];

const SPAN_LABEL: Partial<Record<BootStage, string>> = {
  "app-mounted": "auth0",
  "user-ready": "current user",
  "flags-ready": "feature flags",
  "integrations-ready": "integrations",
};

const PREFIX = "weld:boot:";

function isSupported() {
  return (
    typeof performance !== "undefined" &&
    typeof performance.mark === "function" &&
    typeof performance.measure === "function" &&
    typeof performance.getEntriesByName === "function"
  );
}

const reached = new Set<BootStage>();

export function isBootStageMarked(stage: BootStage) {
  return reached.has(stage);
}

export function markBootStage(stage: BootStage) {
  if (reached.has(stage)) return;
  reached.add(stage);
  if (!isSupported()) return;
  performance.mark(PREFIX + stage, {
    detail: { devtools: { dataType: "marker" } },
  });
  const previous = ORDER[ORDER.indexOf(stage) - 1];
  if (previous && reached.has(previous)) {
    performance.measure(SPAN_LABEL[stage] ?? stage, {
      start: PREFIX + previous,
      end: PREFIX + stage,
      detail: { devtools: { dataType: "track-entry", track: "Weld boot" } },
    });
  }
}

export function bootStageTimings(): Partial<Record<`ms_${string}`, number>> {
  if (!isSupported()) return {};
  const timings: Partial<Record<`ms_${string}`, number>> = {};
  for (const stage of REPORTED_STAGES) {
    const [first] = performance.getEntriesByName(PREFIX + stage, "mark");
    if (first) {
      timings[`ms_${stage.replace("-", "_")}`] = Math.round(first.startTime);
    }
  }
  return timings;
}
